import React from "react";
import { Clock, Warning } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const RISK_TONE = {
  High: "bg-red-50 border-red-200 text-red-800",
  Medium: "bg-amber-50 border-amber-200 text-amber-800",
  Low: "bg-emerald-50 border-emerald-200 text-emerald-800",
};

function ageBucket(d) {
  if (d >= 90) return { label: "90+ days", tone: "bg-red-100 text-red-700" };
  if (d >= 30) return { label: "30+ days", tone: "bg-amber-100 text-amber-700" };
  if (d >= 14) return { label: "14+ days", tone: "bg-orange-100 text-orange-700" };
  if (d >= 7) return { label: "7+ days", tone: "bg-yellow-100 text-yellow-700" };
  return { label: "< 7 days", tone: "bg-emerald-100 text-emerald-700" };
}

export default function PendingSinceTable({ items, hrefBuilder, showCompany = false }) {
  if (!items?.length) {
    return (
      <div className="rounded-md border border-emerald-200 bg-emerald-50 p-6 text-center text-sm text-emerald-800" data-testid="pending-since-empty">
        🎉 No pending observations — every issue has been resolved.
      </div>
    );
  }
  return (
    <div className="rounded-md border border-slate-200 bg-white overflow-hidden" data-testid="pending-since-table">
      <div className="bg-slate-50 px-4 py-2.5 flex items-center justify-between border-b border-slate-200">
        <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
          <Clock size={14} /> Pending Since
        </div>
        <span className="text-[10px] uppercase tracking-wider text-slate-500">Oldest first · top 15</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
            <tr>
              <th className="px-4 py-2 text-left">Sr.</th>
              <th className="px-4 py-2 text-left">Risk</th>
              <th className="px-4 py-2 text-left">Area</th>
              <th className="px-4 py-2 text-left">Observation</th>
              {showCompany && <th className="px-4 py-2 text-left">Client</th>}
              <th className="px-4 py-2 text-left">Open For</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => {
              const ab = ageBucket(it.days_open);
              const href = hrefBuilder ? hrefBuilder(it) : null;
              return (
                <tr key={it.id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`pending-row-${it.id}`}>
                  <td className="px-4 py-2.5 tabular-nums">{it.sr_no || "—"}</td>
                  <td className="px-4 py-2.5">
                    <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border ${RISK_TONE[it.risk] || RISK_TONE.Medium}`}>
                      {it.risk}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 font-medium text-slate-800">{it.area}</td>
                  <td className="px-4 py-2.5 text-slate-700 max-w-md">
                    {href ? <Link to={href} className="hover:underline">{it.observation}</Link> : it.observation}
                  </td>
                  {showCompany && (
                    <td className="px-4 py-2.5 text-slate-600 text-xs">{it.company_name || it.company_id || ""}</td>
                  )}
                  <td className="px-4 py-2.5">
                    <span className={`text-[11px] font-semibold tabular-nums px-2 py-0.5 rounded ${ab.tone}`}>
                      {it.days_open}d <span className="opacity-70 ml-0.5">{ab.label}</span>
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="bg-slate-50 px-4 py-2 text-[11px] text-slate-500 flex items-center gap-1.5 border-t border-slate-200">
        <Warning size={11} /> Items open for over 30 days warrant immediate attention.
      </div>
    </div>
  );
}
